import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  Clock,
  Copy,
  Eye,
  Share2,
  Trash2,
  CheckCircle,
  AlertCircle,
  XCircle,
  Tag,
  AlertTriangle,
  Search,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  X,
  Link as LinkIcon,
  Users,
  Target,
  BarChart3,
  Activity,
  ChevronUp,
} from "lucide-react";
import { toast } from "react-toastify";
import AuthenticatedNavbar from "../components/AuthenticatedNavbar";
import promotionService from "../components/services/PromotionService";
import Loader from "../components/Loader";

const AppliedDeals = () => {
  const [appliedDeals, setAppliedDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedDeal, setSelectedDeal] = useState(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showRemoveModal, setShowRemoveModal] = useState(false);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [copiedLink, setCopiedLink] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemsPerPage] = useState(8);
  const [expandedDeals, setExpandedDeals] = useState(new Set());

  useEffect(() => {
    fetchAppliedDeals();
  }, []);

  const fetchAppliedDeals = async () => {
    setIsLoading(true);
    try {
      const response = await promotionService.getAllAppliedDeals();
      if (response.data.status_code === "000") {
        setAppliedDeals(response.data.data || []);
      } else {
        toast.error(response.data.reason || "Failed to fetch applied deals");
      }
    } catch (error) {
      toast.error("Failed to fetch applied deals");
    } finally {
      setIsLoading(false);
    }
  };

  const deletePendingAppliedDeal = async (slug) => {
    setIsDeleting(true);
    try {
      const response = await promotionService.removePromotion(slug);
      if (response?.data?.status_code === "000") {
        toast.success(
          response?.data?.reason || "Applied Deal deleted successfully"
        );
        fetchAppliedDeals();
      } else {
        toast.error(response?.data?.reason || "Failed to delete applied deal");
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.reason ||
          "Sorry, Applied Deal could not be deleted"
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "pending":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "rejected":
        return "bg-red-100 text-red-800 border-red-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "approved":
        return <CheckCircle className="w-4 h-4" />;
      case "pending":
        return <Clock className="w-4 h-4" />;
      case "rejected":
        return <XCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      return "N/A";
    }
  };

  const calculateDiscountPercentage = (price, discountedPrice) => {
    if (!price || !discountedPrice) return 0;
    return Math.round(((price - discountedPrice) / price) * 100);
  };

  const isDealExpired = (expiryDate) => {
    if (!expiryDate) return false;
    try {
      const expiry = new Date(expiryDate);
      const now = new Date();
      return expiry < now;
    } catch (error) {
      return false;
    }
  };

  const copyToClipboard = async (text, dealId) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedLink(dealId);
      toast.success("Link copied to clipboard!");
      setTimeout(() => setCopiedLink(null), 2000);
    } catch (error) {
      toast.error("Failed to copy link");
    }
  };

  const handleViewLink = (deal) => {
    setSelectedDeal(deal);
    setShowLinkModal(true);
  };

  const handleRemoveDeal = (deal) => {
    setSelectedDeal(deal);
    setShowRemoveModal(true);
  };

  const confirmRemoveDeal = async (slug) => {
    deletePendingAppliedDeal(slug);
    setShowRemoveModal(false);
    setSelectedDeal(null);
    fetchAppliedDeals();
  };

  const handleViewRejection = (deal) => {
    setSelectedDeal(deal);
    setShowRejectionModal(true);
  };

  const toggleDealExpansion = (dealId) => {
    setExpandedDeals(prev => {
      const newSet = new Set(prev);
      if (newSet.has(dealId)) {
        newSet.delete(dealId);
      } else {
        newSet.add(dealId);
      }
      return newSet;
    });
  };

  const filteredDeals = appliedDeals.filter((deal) => {
    const matchesSearch = deal.deal.title
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    const matchesStatus =
      statusFilter === "all" || deal.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const sortedDeals = [...filteredDeals].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.created_at) - new Date(a.created_at);
      case "oldest":
        return new Date(a.created_at) - new Date(b.created_at);
      case "title":
        return a.deal.title.localeCompare(b.deal.title);
      case "status":
        return a.status.localeCompare(b.status);
      default:
        return 0;
    }
  });

  // Pagination
  const totalPages = Math.ceil(sortedDeals.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedDeals = sortedDeals.slice(startIndex, endIndex);

  const goToPage = (page) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const truncateTitle = (title, maxLength = 20) => {
    if (title.length <= maxLength) return title;
    return title.substring(0, maxLength) + "...";
  };


  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <AuthenticatedNavbar />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-4 py-6 mx-auto mt-16 max-w-8xl sm:px-6 lg:px-8">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  Applied Deals
                </h1>
                <p className="mt-2 text-gray-600">
                  Track and manage your affiliate deal applications
                </p>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={fetchAppliedDeals}
                  className="flex gap-2 items-center px-4 py-2 text-gray-700 bg-white rounded-lg border border-gray-300 transition-colors hover:bg-gray-50"
                >
                  <Search className="w-4 h-4" />
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="px-4 py-6 mx-auto max-w-8xl sm:px-6 lg:px-8">
          <div className="p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
            <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search deals..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent"
                />
              </div>

              {/* Filters */}
              <div className="flex gap-3">
                <div className="relative">
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent appearance-none bg-white pr-8"
                  >
                    <option value="all">All Status</option>
                    <option value="pending">Pending</option>
                    <option value="approved">Approved</option>
                    <option value="rejected">Rejected</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                </div>

                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#F2640F] focus:border-transparent appearance-none bg-white pr-8"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="title">By Title</option>
                    <option value="status">By Status</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 w-4 h-4 text-gray-400 transform -translate-y-1/2 pointer-events-none" />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8 mx-auto max-w-8xl sm:px-6 lg:px-8">
          {sortedDeals.length === 0 ? (
            /* Empty State */
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-col justify-center items-center py-16 text-center"
            >
              <div className="flex justify-center items-center mb-6 w-24 h-24 bg-gray-100 rounded-full">
                <Share2 className="w-12 h-12 text-gray-400" />
              </div>
              <h3 className="mb-2 text-xl font-semibold text-gray-900">
                {searchTerm || statusFilter !== "all"
                  ? "No deals found"
                  : "No applied deals yet"}
              </h3>
              <p className="mb-6 max-w-md text-gray-600">
                {searchTerm || statusFilter !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "Start promoting deals by applying to become an affiliate for your favorite brands"}
              </p>
              {!searchTerm && statusFilter === "all" && (
                <button
                  onClick={() => window.history.back()}
                  className="px-6 py-3 bg-gradient-to-r from-[#F2640F] to-[#FF0000] text-white font-semibold rounded-lg hover:shadow-lg transition-all duration-300"
                >
                  Browse Deals
                </button>
              )}
            </motion.div>
          ) : (
            /* Accordion Timeline Layout - Mobile Optimized */
            <div className="space-y-3 sm:space-y-4">
              {paginatedDeals.map((deal, index) => {
                const isExpanded = expandedDeals.has(deal.id);
                return (
                  <motion.div
                    key={deal.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                    className="overflow-hidden bg-white rounded-xl border border-gray-200 shadow-sm transition-all duration-300 sm:rounded-2xl hover:shadow-lg"
                  >
                    {/* Compact Header Section - Mobile Optimized */}
                    <div 
                      className="relative p-3 bg-gradient-to-r from-gray-50 to-white transition-all duration-200 cursor-pointer sm:p-4 hover:from-gray-100 hover:to-gray-50"
                      onClick={() => toggleDealExpansion(deal.id)}
                    >
                      <div className="flex gap-2 justify-between items-center sm:gap-4">
                        {/* Deal Info */}
                        <div className="flex flex-1 gap-2 items-center min-w-0 sm:gap-4">
                          <div className="flex-shrink-0">
                            <img
                              src={deal.deal.image}
                              alt={deal.deal.title}
                              className="object-cover w-12 h-12 rounded-lg shadow-sm sm:w-16 sm:h-16 sm:rounded-xl"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="mb-1 text-sm font-bold text-gray-900 truncate sm:text-lg">
                              {deal.deal.title}
                            </h3>
                            <div className="flex flex-wrap gap-2 items-center mb-1 sm:gap-3 sm:mb-2">
                              <div className="flex gap-1 items-center sm:gap-2">
                                <span className="text-lg font-bold text-green-600 sm:text-xl">
                                  GH₵{deal.deal.discounted_price || deal.deal.price}
                                </span>
                                {deal.deal.discounted_price && (
                                  <>
                                    <span className="text-xs text-gray-500 line-through sm:text-sm">
                                      GH₵{deal.deal.price}
                                    </span>
                                    <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                                      -{calculateDiscountPercentage(deal.deal.price, deal.deal.discounted_price)}%
                                    </span>
                                  </>
                                )}
                              </div>
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              {deal.deal.category.slice(0, 1).map((cat, idx) => (
                                <span key={idx} className="inline-flex gap-1 items-center px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium text-gray-600 bg-gray-100 rounded-full">
                                  <Tag className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                  <span className="hidden sm:inline">{cat}</span>
                                  <span className="sm:hidden">{cat.substring(0, 8)}...</span>
                                </span>
                              ))}
                              {deal.deal.category.length > 1 && (
                                <span className="px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs text-gray-500">
                                  +{deal.deal.category.length - 1}
                                </span>
                              )}
                              {isDealExpired(deal.deal.expiry_date) && (
                                <span className="inline-flex gap-1 items-center px-1.5 py-0.5 sm:px-2 sm:py-1 text-xs font-medium text-red-600 bg-red-100 rounded-full">
                                  <Clock className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
                                  <span className="hidden sm:inline">Expired</span>
                                  <span className="sm:hidden">Exp</span>
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status and Expand Button - Mobile Optimized */}
                        <div className="flex flex-shrink-0 gap-1 items-center sm:gap-3">
                          <span className={`inline-flex items-center gap-1 sm:gap-2 px-2 py-1 sm:px-3 sm:py-1.5 text-xs sm:text-sm font-semibold rounded-full border ${getStatusColor(deal.status)}`}>
                            <span className="hidden sm:inline">{getStatusIcon(deal.status)}</span>
                            <span className="sm:hidden">{deal.status.charAt(0).toUpperCase()}</span>
                            <span className="hidden sm:inline">{deal.status.charAt(0).toUpperCase() + deal.status.slice(1)}</span>
                          </span>
                          <button className="p-1 text-gray-400 transition-colors sm:p-2 hover:text-gray-600">
                            {isExpanded ? (
                              <ChevronUp className="w-4 h-4 sm:w-5 sm:h-5" />
                            ) : (
                              <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>

                    {/* Expandable Timeline Section */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.3, ease: "easeInOut" }}
                          className="overflow-hidden"
                        >
                          <div className="p-4 border-t border-gray-100 sm:p-6">
                            <div className="mb-4 sm:mb-6">
                              <h4 className="flex gap-2 items-center mb-3 text-base font-semibold text-gray-900 sm:text-lg sm:mb-4">
                                <Activity className="w-4 h-4 sm:w-5 sm:h-5 text-[#F2640F]" />
                                Application Timeline
                              </h4>
                              <div className="relative">
                                {/* Timeline Line */}
                                <div className="absolute left-6 sm:left-8 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                                
                                {/* Timeline Steps */}
                                <div className="space-y-4 sm:space-y-6">
                                  {/* Applied Step */}
                                  <div className="flex relative gap-3 items-start sm:gap-4">
                                    <div className="flex relative z-10 flex-shrink-0 justify-center items-center w-12 h-12 bg-blue-100 rounded-full sm:w-16 sm:h-16">
                                      <Calendar className="w-5 h-5 text-blue-600 sm:w-6 sm:h-6" />
                                    </div>
                                    <div className="flex-1 pt-1 sm:pt-2">
                                      <div className="flex flex-col gap-1 mb-1 sm:flex-row sm:items-center sm:gap-2">
                                        <h5 className="text-sm font-semibold text-gray-900 sm:text-base">Application Submitted</h5>
                                        <span className="px-2 py-1 text-xs font-medium text-blue-600 bg-blue-100 rounded-full w-fit">Completed</span>
                                      </div>
                                      <p className="mb-1 text-xs text-gray-600 sm:text-sm sm:mb-2">Your application was successfully submitted</p>
                                      <p className="text-xs text-gray-500">{formatDate(deal.created_at)}</p>
                                    </div>
                                  </div>

                                  {/* Pending/Approved/Rejected Step */}
                                  <div className="flex relative gap-3 items-start sm:gap-4">
                                    <div className={`flex-shrink-0 w-12 h-12 sm:w-16 sm:h-16 rounded-full flex items-center justify-center relative z-10 ${
                                      deal.status === "pending" ? "bg-yellow-100" :
                                      deal.status === "approved" ? "bg-green-100" : "bg-red-100"
                                    }`}>
                                      {deal.status === "pending" ? (
                                        <Clock className="w-5 h-5 text-yellow-600 sm:w-6 sm:h-6" />
                                      ) : deal.status === "approved" ? (
                                        <CheckCircle className="w-5 h-5 text-green-600 sm:w-6 sm:h-6" />
                                      ) : (
                                        <XCircle className="w-5 h-5 text-red-600 sm:w-6 sm:h-6" />
                                      )}
                                    </div>
                                    <div className="flex-1 pt-1 sm:pt-2">
                                      <div className="flex flex-col gap-1 mb-1 sm:flex-row sm:items-center sm:gap-2">
                                        <h5 className="text-sm font-semibold text-gray-900 sm:text-base">
                                          {deal.status === "pending" ? "Under Review" :
                                           deal.status === "approved" ? "Approved" : "Rejected"}
                                        </h5>
                                        <span className={`px-2 py-1 text-xs font-medium rounded-full w-fit ${
                                          deal.status === "pending" ? "text-yellow-600 bg-yellow-100" :
                                          deal.status === "approved" ? "text-green-600 bg-green-100" : "text-red-600 bg-red-100"
                                        }`}>
                                          {deal.status === "pending" ? "In Progress" :
                                           deal.status === "approved" ? "Completed" : "Completed"}
                                        </span>
                                      </div>
                                      <p className="mb-1 text-xs text-gray-600 sm:text-sm sm:mb-2">
                                        {deal.status === "pending" ? "Your application is being reviewed by our team" :
                                         deal.status === "approved" ? "Congratulations! Your application has been approved" :
                                         "Unfortunately, your application was not approved"}
                                      </p>
                                      {deal.approved_date && (
                                        <p className="text-xs text-gray-500">Approved: {formatDate(deal.approved_date)}</p>
                                      )}
                                      {deal.rejected_date && (
                                        <p className="text-xs text-gray-500">Rejected: {formatDate(deal.rejected_date)}</p>
                                      )}
                                    </div>
                                  </div>

                                  {/* Performance Section (Only for approved deals) */}
                                  {deal.status === "approved" && (
                                    <div className="flex relative gap-3 items-start sm:gap-4">
                                      <div className="flex relative z-10 flex-shrink-0 justify-center items-center w-12 h-12 bg-purple-100 rounded-full sm:w-16 sm:h-16">
                                        <BarChart3 className="w-5 h-5 text-purple-600 sm:w-6 sm:h-6" />
                                      </div>
                                      <div className="flex-1 pt-1 sm:pt-2">
                                        <div className="flex flex-col gap-1 mb-1 sm:flex-row sm:items-center sm:gap-2">
                                          <h5 className="text-sm font-semibold text-gray-900 sm:text-base">Performance Tracking</h5>
                                          <span className="px-2 py-1 text-xs font-medium text-purple-600 bg-purple-100 rounded-full w-fit">Live</span>
                                        </div>
                                        <p className="mb-3 text-xs text-gray-600 sm:text-sm sm:mb-4">Track your promotion performance and engagement</p>
                                        
                                        {/* Performance Metrics - Mobile Optimized */}
                                        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 sm:gap-4">
                                          <div className="p-3 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 sm:p-4 sm:rounded-xl">
                                            <div className="flex gap-2 items-center mb-2 sm:gap-3">
                                              <div className="p-1.5 sm:p-2 bg-blue-200 rounded-lg">
                                                <Users className="w-3 h-3 text-blue-600 sm:w-4 sm:h-4" />
                                              </div>
                                              <div>
                                                <p className="text-xs font-medium text-blue-900 sm:text-sm">Redemptions</p>
                                                <p className="hidden text-xs text-blue-700 sm:block">People who got codes</p>
                                              </div>
                                            </div>
                                            <p className="text-xl font-bold text-blue-900 sm:text-2xl">
                                              {deal.stats.redeemed_count || 0}
                                            </p>
                                          </div>

                                          <div className="p-3 bg-gradient-to-br from-green-50 to-green-100 rounded-lg border border-green-200 sm:p-4 sm:rounded-xl">
                                            <div className="flex gap-2 items-center mb-2 sm:gap-3">
                                              <div className="p-1.5 sm:p-2 bg-green-200 rounded-lg">
                                                <Target className="w-3 h-3 text-green-600 sm:w-4 sm:h-4" />
                                              </div>
                                              <div>
                                                <p className="text-xs font-medium text-green-900 sm:text-sm">Completed</p>
                                                <p className="hidden text-xs text-green-700 sm:block">Used in store</p>
                                              </div>
                                            </div>
                                            <p className="text-xl font-bold text-green-900 sm:text-2xl">
                                              {deal.stats.completed_count || 0}
                                            </p>
                                          </div>

                                          <div className="p-3 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 sm:p-4 sm:rounded-xl sm:col-span-2 lg:col-span-1">
                                            <div className="flex gap-2 items-center mb-2 sm:gap-3">
                                              <div className="p-1.5 sm:p-2 bg-purple-200 rounded-lg">
                                                <BarChart3 className="w-3 h-3 text-purple-600 sm:w-4 sm:h-4" />
                                              </div>
                                              <div>
                                                <p className="text-xs font-medium text-purple-900 sm:text-sm">Conversion</p>
                                                <p className="hidden text-xs text-purple-700 sm:block">Success rate</p>
                                              </div>
                                            </div>
                                            <p className="text-xl font-bold text-purple-900 sm:text-2xl">
                                              {deal.stats.redeemed_count > 0 ? Math.round((deal.stats.completed_count / deal.stats.redeemed_count) * 100) : 0}%
                                            </p>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>

                            {/* Actions Section - Mobile Optimized */}
                            <div className="flex flex-col gap-2 pt-3 border-t border-gray-100 sm:gap-3 sm:pt-4">
                              {/* Main Action Button */}
                              {deal.status === "approved" && deal.shareable_link ? (
                                <button
                                  onClick={() => handleViewLink(deal)}
                                  disabled={isDealExpired(deal.deal.expiry_date)}
                                  className={`flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-medium rounded-lg sm:rounded-xl transition-all duration-200 ${
                                    isDealExpired(deal.deal.expiry_date)
                                      ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                                      : "text-white bg-gradient-to-r from-[#F2640F] to-[#FF0000] hover:shadow-lg"
                                  }`}
                                >
                                  <Share2 className="w-4 h-4" />
                                  <span className="hidden sm:inline">
                                    {isDealExpired(deal.deal.expiry_date) ? "Deal Expired" : "Share Deal"}
                                  </span>
                                  <span className="sm:hidden">
                                    {isDealExpired(deal.deal.expiry_date) ? "Expired" : "Share"}
                                  </span>
                                </button>
                              ) : deal.status === "rejected" ? (
                                <button
                                  onClick={() => handleViewRejection(deal)}
                                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-medium text-red-600 bg-red-100 rounded-lg sm:rounded-xl hover:bg-red-200 transition-colors"
                                >
                                  <Eye className="w-4 h-4" />
                                  <span className="hidden sm:inline">View Rejection Details</span>
                                  <span className="sm:hidden">View Details</span>
                                </button>
                              ) : (
                                <button
                                  disabled
                                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-medium text-gray-500 bg-gray-100 rounded-lg sm:rounded-xl cursor-not-allowed"
                                >
                                  <Clock className="w-4 h-4" />
                                  <span className="hidden sm:inline">{deal.status === "pending" ? "Pending Approval" : "Not Available"}</span>
                                  <span className="sm:hidden">{deal.status === "pending" ? "Pending" : "N/A"}</span>
                                </button>
                              )}

                              {/* Remove Button */}
                              <button
                                onClick={() => handleRemoveDeal(deal)}
                                disabled={deal.status === "approved"}
                                className={`flex items-center justify-center gap-2 px-3 py-2.5 sm:px-4 sm:py-3 text-sm font-medium rounded-lg sm:rounded-xl transition-colors ${
                                  deal.status === "approved"
                                    ? "text-gray-400 bg-gray-50 cursor-not-allowed"
                                    : "text-gray-600 bg-gray-100 hover:bg-gray-200"
                                }`}
                              >
                                <Trash2 className="w-4 h-4" />
                                <span className="hidden sm:inline">Remove</span>
                                <span className="sm:hidden">Delete</span>
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {sortedDeals.length > itemsPerPage && (
            <div className="flex justify-center mt-8">
              <div className="flex gap-2 items-center">
                <button
                  onClick={() => goToPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="flex gap-1 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Previous
                </button>

                <div className="flex gap-1">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                    (page) => (
                      <button
                        key={page}
                        onClick={() => goToPage(page)}
                        className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                          currentPage === page
                            ? "bg-[#F2640F] text-white"
                            : "text-gray-700 bg-white border border-gray-300 hover:bg-gray-50"
                        }`}
                      >
                        {page}
                      </button>
                    )
                  )}
                </div>

                <button
                  onClick={() => goToPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="flex gap-1 items-center px-3 py-2 text-sm font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Link Modal */}
      <AnimatePresence>
        {showLinkModal && selectedDeal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowLinkModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-lg bg-white rounded-2xl shadow-2xl"
            >
              <div className="p-6">
                <div className="flex gap-3 items-center mb-4">
                  <div className="flex justify-center items-center w-10 h-10 bg-green-100 rounded-full">
                    <LinkIcon className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Shareable Link
                    </h3>
                    <p className="text-sm text-gray-600">
                      {truncateTitle(selectedDeal.deal.title, 50)}
                    </p>
                  </div>
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="p-2 ml-auto text-gray-400 transition-colors hover:text-gray-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-6">
                  <label className="block mb-2 text-sm font-medium text-gray-700">
                    Your Affiliate Link
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={selectedDeal.shareable_link}
                      readOnly
                      className={`w-full px-4 py-3 pr-12 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:border-transparent ${
                        isDealExpired(selectedDeal.deal.expiry_date)
                          ? "bg-gray-100 border-gray-300 text-gray-500"
                          : "bg-gray-50 border-gray-200 focus:ring-[#F2640F]"
                      }`}
                    />
                    <button
                      onClick={() =>
                        copyToClipboard(
                          selectedDeal.shareable_link,
                          selectedDeal.id
                        )
                      }
                      disabled={isDealExpired(selectedDeal.deal.expiry_date)}
                      className={`absolute right-2 top-1/2 transform -translate-y-1/2 p-2 transition-colors ${
                        isDealExpired(selectedDeal.deal.expiry_date)
                          ? "text-gray-300 cursor-not-allowed"
                          : "text-gray-400 hover:text-[#F2640F]"
                      }`}
                    >
                      {copiedLink === selectedDeal.id ? (
                        <CheckCircle className="w-4 h-4" />
                      ) : (
                        <Copy className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                  {isDealExpired(selectedDeal.deal.expiry_date) && (
                    <p className="mt-2 text-xs text-red-600">
                      ⚠️ This deal has expired and cannot be shared
                    </p>
                  )}
                </div>

                <div className="p-4 mb-6 bg-blue-50 rounded-lg border border-blue-200">
                  <h4 className="mb-2 text-sm font-medium text-blue-800">
                    💡 Pro Tips:
                  </h4>
                  <ul className="space-y-1 text-xs text-blue-700">
                    <li>• Share this link on your social media platforms</li>
                    <li>• Include it in your blog posts or website</li>
                    <li>
                      • Send it directly to your audience via email or messaging
                    </li>
                    <li>• Track your performance in the earnings section</li>
                  </ul>
                </div>

                <div className="flex gap-3 justify-end">
                  <button
                    onClick={() => setShowLinkModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                  >
                    Close
                  </button>
                  <button
                    onClick={() =>
                      copyToClipboard(
                        selectedDeal.shareable_link,
                        selectedDeal.id
                      )
                    }
                    disabled={isDealExpired(selectedDeal.deal.expiry_date)}
                    className={`px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${
                      isDealExpired(selectedDeal.deal.expiry_date)
                        ? "text-gray-400 bg-gray-100 cursor-not-allowed"
                        : "text-white bg-gradient-to-r from-[#F2640F] to-[#FF0000] hover:shadow-md"
                    }`}
                  >
                    {copiedLink === selectedDeal.id ? "Copied!" : "Copy Link"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Rejection Reason Modal */}
      <AnimatePresence>
        {showRejectionModal && selectedDeal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowRejectionModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              <div className="p-6">
                <div className="flex gap-3 items-center mb-4">
                  <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
                    <XCircle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Application Rejected
                    </h3>
                    <p className="text-sm text-gray-600">
                      {selectedDeal.deal.title}
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-red-50 rounded-lg border border-red-200">
                  <h4 className="mb-2 text-sm font-medium text-red-800">
                    Rejection Details:
                  </h4>
                  <div className="space-y-2 text-sm text-red-700">
                    {selectedDeal.reject_reason ? (
                      <p>{selectedDeal.reject_reason}</p>
                    ) : (
                      <p className="italic">
                        No specific reason provided for rejection.
                      </p>
                    )}
                    {selectedDeal.rejected_date && (
                      <p className="text-xs text-red-600">
                        Rejected on: {formatDate(selectedDeal.rejected_date)}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex justify-end mt-6">
                  <button
                    onClick={() => setShowRejectionModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Remove Confirmation Modal */}
      <AnimatePresence>
        {showRemoveModal && selectedDeal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="flex fixed inset-0 z-50 justify-center items-center p-4 bg-black bg-opacity-50 backdrop-blur-sm"
            onClick={() => setShowRemoveModal(false)}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              onClick={(e) => e.stopPropagation()}
              className="w-full max-w-md bg-white rounded-2xl shadow-2xl"
            >
              <div className="p-6">
                <div className="flex gap-3 items-center mb-4">
                  <div className="flex justify-center items-center w-10 h-10 bg-red-100 rounded-full">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Remove Deal Application
                    </h3>
                    <p className="text-sm text-gray-600">
                      This action cannot be undone
                    </p>
                  </div>
                </div>

                <div className="p-4 bg-gray-50 rounded-lg">
                  <p className="text-sm text-gray-700">
                    Are you sure you want to remove your application for{" "}
                    <strong>{selectedDeal.deal.title}</strong>? You'll need to
                    reapply if you want to promote this deal again.
                  </p>
                </div>

                <div className="flex gap-3 justify-end mt-6">
                  <button
                    onClick={() => setShowRemoveModal(false)}
                    className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg transition-colors hover:bg-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={()=>confirmRemoveDeal(selectedDeal.deal.deal_slug)}
                    disabled={isDeleting}
                    className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg transition-colors hover:bg-red-700"
                  >
                    {isDeleting ? "Removing..." : "Remove"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default AppliedDeals;